import { Link } from 'react-router-dom';
import type { Movie } from '@/mocks/movies';

interface MovieCardProps {
  movie: Movie;
  variant?: 'default' | 'compact' | 'wide';
}

export default function MovieCard({ movie, variant = 'default' }: MovieCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${movie.id}`}
        className="group flex items-center gap-3 p-2 rounded-lg hover:bg-background-100 transition-colors"
      >
        <div className="w-12 h-16 flex-shrink-0 rounded overflow-hidden">
          <img
            src={movie.poster}
            alt={movie.title}
            className="w-full h-full object-cover"
            width="48"
            height="64"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm text-foreground-100 font-medium truncate group-hover:text-primary-400 transition-colors">
            {movie.title}
          </h4>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-xs text-primary-500">
              <i className="ri-star-fill mr-0.5"></i>
              {movie.rating}
            </span>
            <span className="text-xs text-foreground-500">{movie.year}</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'wide') {
    return (
      <Link
        to={`/article/${movie.id}`}
        className="group flex gap-4 p-3 rounded-lg hover:bg-background-100 transition-colors"
      >
        <div className="w-[120px] h-[160px] flex-shrink-0 rounded-lg overflow-hidden">
          <img
            src={movie.poster}
            alt={movie.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            width="120"
            height="160"
          />
        </div>
        <div className="flex-1 min-w-0 flex flex-col justify-between">
          <div>
            <h3 className="text-base font-semibold text-foreground-100 group-hover:text-primary-400 transition-colors">
              {movie.title}
            </h3>
            <p className="text-xs text-foreground-500 mt-1 line-clamp-2">{movie.description}</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-xs text-primary-500 font-semibold">
              <i className="ri-star-fill mr-0.5"></i>
              {movie.rating}
            </span>
            <span className="text-xs text-foreground-500">{movie.year}</span>
            <span className="text-xs text-foreground-500">{movie.duration}</span>
            <span className="text-xs text-foreground-600">{movie.director} 导演</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${movie.id}`}
      className="group block"
    >
      <div className="relative aspect-[2/3] rounded-lg overflow-hidden mb-3">
        <img
          src={movie.poster}
          alt={movie.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          width="300"
          height="450"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
        <div className="absolute top-2 left-2">
          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-primary-500 text-background-950">
            {movie.rating}
          </span>
        </div>
        {movie.isNew && (
          <div className="absolute top-2 right-2">
            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-accent-500 text-foreground-50">
              NEW
            </span>
          </div>
        )}
        <div className="absolute bottom-2 left-2 right-2">
          <div className="flex items-center gap-1 flex-wrap">
            {movie.tags.slice(0, 2).map((tag) => (
              <span key={tag} className="px-1.5 py-0.5 rounded-sm text-[9px] bg-background-950/60 text-foreground-300">
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>
      <h3 className="text-sm font-medium text-foreground-100 group-hover:text-primary-400 transition-colors line-clamp-1">
        {movie.title}
      </h3>
      <p className="text-xs text-foreground-500 mt-0.5">{movie.year} · {movie.duration}</p>
    </Link>
  );
}